<template>
  <section class="team-member-page">
    <PageHeader :back-route="{ name: 'user.my-team' }" :title="pageTitle" icon="uil-user-check"/>

    <div v-if="notFound" class="team-page-state team-page-state--empty" role="alert">
      <i aria-hidden="true" class="uil-user-times"></i>
      <div>
        <strong>{{ $t('general.team_member_not_found') }}</strong>
        <p>{{ $t('general.team_member_not_found_hint') }}</p>
      </div>
      <router-link :to="{ name: 'user.my-team' }" class="team-state-link">
        {{ $t('general.back_to_my_team') }}
      </router-link>
    </div>

    <div v-else-if="pageError" class="team-page-state team-page-state--error" role="alert">
      <i aria-hidden="true" class="uil-exclamation-triangle"></i>
      <div>
        <strong>{{ $t('general.team_member_load_failed') }}</strong>
        <p>{{ pageError }}</p>
      </div>
      <button class="team-state-button" type="button" @click="reloadPage">
        {{ $t('general.try_again') }}
      </button>
    </div>

    <template v-else>
      <section v-if="member" :aria-label="$t('general.team_member_info')" class="member-summary">
        <div class="member-summary__identity">
          <UserProfileAvatar :name="memberName" :photo="member.avatar || ''" class="member-summary-avatar"/>
          <div>
            <strong>{{ memberName }}</strong>
            <a v-if="member.email" >{{ member.email }}</a>
          </div>
          <button :aria-label="$t('general.view_organization_info')"
                  class="member-summary__info"
                  type="button"
                  @click="orgModalVisible = true">
            <i aria-hidden="true" class="uil-info-circle"></i>
          </button>
        </div>
        <dl class="member-summary__organization">
          <div v-for="field in organizationFields" :key="field.key">
            <dt>{{ field.label }}</dt>
            <dd>{{ displayValue(member[field.key]) }}</dd>
          </div>
        </dl>
      </section>

      <section v-else-if="loading"
               :aria-label="$t('general.loading_team_member')"
               aria-busy="true"
               class="member-summary member-summary--skeleton"
               role="status">
        <span class="skl skl--avatar"></span>
        <div class="member-summary-skeleton__lines">
          <span class="skl skl--title"></span>
          <span class="skl skl--line"></span>
        </div>
      </section>

      <AppModal :title="$t('general.employee_detail_with_name', { name: memberName })"
                :visible.sync="orgModalVisible"
                aria-modal="true"
                max-height="90vh"
                role="dialog"
                width="480px"
                @close="orgModalVisible = false">
        <dl v-if="member" class="member-org-meta">
          <div v-for="field in organizationFields" :key="field.key">
            <dt>{{ field.label }}</dt>
            <dd>{{ displayValue(member[field.key]) }}</dd>
          </div>
        </dl>
        <template #footer>
          <button class="member-org-close" type="button" @click="orgModalVisible = false">
            {{ $t('general.close') }}
          </button>
        </template>
      </AppModal>

      <div class="member-controls">
        <div :aria-label="$t('general.total_competencies')" class="member-total">
          <i aria-hidden="true" class="uil-award"></i>
          <span>{{ $t('general.total_competencies') }}</span>
          <strong>{{ loading ? "—" : total }}</strong>
        </div>

        <div class="member-toolbar">
          <label class="member-search">
            <i aria-hidden="true" class="uil-search"></i>
            <span class="sr-only">{{ $t('general.search_competency') }}</span>
            <input v-model="searchText"
                   :placeholder="$t('general.search_placeholder')"
                   autocomplete="off"
                   type="search">
          </label>
          <multiple-select-input v-model="selectedSort"
                                 :allowEmpty="false"
                                 :multiple="false"
                                 :required="false"
                                 :searchable="false"
                                 :showLabels="false"
                                 :values="sortOptions"
                                 class="member-sort"
                                 @select="onSortChange"
          />
        </div>
      </div>

      <div v-if="loading" aria-busy="true" aria-live="polite" class="member-competency-loading">
        <div class="member-competency-grid">
          <CompetencyCardSkeleton v-for="n in skeletonCount" :key="`skeleton-${n}`"/>
        </div>
        <div aria-hidden="true" class="member-competency-list">
          <div v-for="n in skeletonCount"
               :key="`row-skeleton-${n}`"
               class="competency-row competency-row--skeleton">
            <span class="skl skl--icon"></span>
            <div class="competency-row__body">
              <span class="skl skl--title"></span>
              <span class="skl skl--line"></span>
              <span class="skl skl--meta"></span>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="listError" class="team-list-state team-list-state--error" role="alert">
        <i aria-hidden="true" class="uil-exclamation-triangle"></i>
        <div>
          <strong>{{ $t('general.load_failed') }}</strong>
          <p>{{ listError }}</p>
        </div>
        <button class="team-state-button" type="button" @click="loadCompetencies">
          {{ $t('general.try_again') }}
        </button>
      </div>

      <div v-else-if="!competencies.length" class="team-list-state team-list-state--empty">
        <i aria-hidden="true" class="uil-award"></i>
        <div>
          <strong>
            {{
              searchText.trim()
                  ? $t('general.empty_search')
                  : $t('general.no_competencies_assigned')
            }}
          </strong>
          <p v-if="searchText.trim()">{{ $t('general.empty_search_hint') }}</p>
        </div>
      </div>

      <template v-else>
        <div class="member-competency-grid">
          <CompetencyCard v-for="assignment in competencies"
                          :key="assignment.id"
                          :assignment="assignment"
                          @select="openCompetency"
          />
        </div>

        <div class="member-competency-list">
          <article v-for="assignment in competencies"
                   :key="`row-${assignment.id}`"
                   :aria-label="$t('general.view_competency_detail', { name: assignment.name })"
                   class="competency-row"
                   role="button"
                   tabindex="0"
                   @click="openCompetency(assignment)"
                   @keydown.enter.prevent="openCompetency(assignment)"
                   @keydown.space.prevent="openCompetency(assignment)">
            <span aria-hidden="true" class="competency-row__icon">
              <i class="uil-award"></i>
            </span>
            <div class="competency-row__body">
              <strong>{{ assignment.name }}</strong>
              <p>{{ assignment.description || '' }}</p>
              <small>
                {{ $t('general.assigned_on', { date: formatDate(assignment.assigned_at) }) }}
              </small>
            </div>
            <i aria-hidden="true" class="uil-angle-right-b competency-row__chevron"></i>
          </article>
        </div>

        <Pagination v-if="totalPages > 1"
                    :pagination="pagination"
                    class="member-pagination"
                    @changePage="changePage"
        />
      </template>
    </template>
  </section>
</template>

<script>
import PageHeader from "@/view/components/common/PageHeader.vue";
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import AppModal from "@/view/components/ui/AppModal.vue";
import CompetencyCard from "@/view/components/developmentCompetencies/CompetencyCard.vue";
import CompetencyCardSkeleton from "@/view/components/developmentCompetencies/CompetencyCardSkeleton.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import Pagination from "@/assets/components/Pagination.vue";
import ApiService from "@/core/services/api.service";
import querystring from "querystring";
import moment from "moment";
import widgetMixin from "@/core/mixin/widget.mixin";

export default {
  name: "MyTeamMemberDetail",
  mixins: [widgetMixin],
  components: {
    AppModal,
    UserProfileAvatar,
    CompetencyCard,
    CompetencyCardSkeleton,
    MultipleSelectInput,
    PageHeader,
    Pagination,
  },
  props: {
    userId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      member: null,
      competencies: [],
      total: 0,
      searchText: "",
      selectedSort: null,
      page: 1,
      perPage: 15,
      totalPages: 1,
      loading: true,
      notFound: false,
      pageError: "",
      listError: "",
      searchTimer: null,
      requestSequence: 0,
      orgModalVisible: false,
    };
  },
  computed: {
    currentUserId() {
      return this.userId !== null && this.userId !== undefined
          ? this.userId
          : this.$route.params.userId;
    },
    memberName() {
      return this.member?.full_name || this.$t("general.team_member");
    },
    pageTitle() {
      return this.member
          ? this.$t("general.team_member_competencies_with_name", {
            name: this.memberName,
          })
          : this.$t("general.team_member_competencies");
    },
    organizationFields() {
      return [
        { key: "region_name", label: this.$t("profile.gm_region") },
        { key: "department_name", label: this.$t("profile.unit") },
        { key: "branch_name", label: this.$t("profile.branch") },
        { key: "position_name", label: this.$t("users.mission") },
        { key: "title_name", label: this.$t("profile.title") },
      ];
    },
    skeletonCount() {
      return Math.min(this.perPage, 6);
    },
    sortOptions() {
      return [
        { id: "desc", name: this.$t("general.sort_newest") },
        { id: "asc", name: this.$t("general.sort_oldest") },
      ];
    },
    pagination() {
      return {
        current_page: this.page,
        last_page: this.totalPages,
        per_page: this.perPage,
        total: this.total,
      };
    },
  },
  watch: {
    currentUserId() {
      this.reloadPage();
    },
    searchText() {
      clearTimeout(this.searchTimer);
      this.requestSequence += 1;
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.loadCompetencies();
      }, 350);
    },
  },
  created() {
    this.syncSelectedSort();
  },
  async mounted() {
    // Widget kapalıysa sayfa erişime kapalı (sol menü ile aynı kural)
    try {
      await this.getWidgetsMixin();

      if (!this.isMyTeamActiveMixin) {
        await this.$router.push({ path: "/dashboard" });
        return;
      }

      await this.reloadPage();
    } catch (error) {
      await this.$router.push({ path: "/dashboard" });
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.requestSequence += 1;
  },
  methods: {
    hasValidUserId() {
      const id = String(this.currentUserId ?? "").trim();
      return id.length > 0 && id.length <= 200;
    },
    syncSelectedSort() {
      const currentId = this.selectedSort?.id || "desc";
      this.selectedSort =
          this.sortOptions.find((option) => option.id === currentId) ||
          this.sortOptions[0];
    },
    async reloadPage() {
      clearTimeout(this.searchTimer);
      this.orgModalVisible = false;
      this.member = null;
      this.competencies = [];
      this.total = 0;
      this.page = 1;
      this.totalPages = 1;
      this.searchText = "";
      this.notFound = false;
      this.pageError = "";
      this.listError = "";
      this.syncSelectedSort();
      await this.loadCompetencies({ initial: true });
    },
    onSortChange(option) {
      this.selectedSort = option || this.sortOptions[0];
      this.page = 1;
      this.loadCompetencies();
    },
    async loadCompetencies({ initial = false } = {}) {
      if (!this.hasValidUserId()) {
        this.notFound = true;
        this.loading = false;
        return;
      }

      const requestId = ++this.requestSequence;
      this.loading = true;
      this.listError = "";
      if (initial) {
        this.pageError = "";
        this.notFound = false;
      }

      try {
        const response = await ApiService.get(
            `api/my-team/${this.currentUserId}?${querystring.stringify({
              search: this.searchText.trim(),
              order_by: "created_at",
              sort: this.selectedSort?.id || "desc",
              page: this.page,
              perPage: this.perPage,
            })}`
        );

        if (requestId !== this.requestSequence) return;

        const payload = response?.data || {};
        const competenciesPayload =
            payload.competencies && typeof payload.competencies === "object"
                ? payload.competencies
                : payload;
        const meta = competenciesPayload.meta || {};
        const rows = Array.isArray(competenciesPayload.data)
            ? competenciesPayload.data
            : [];
        const total = Number(meta.total);

        if (payload.user && typeof payload.user === "object") {
          this.member = payload.user;
        } else if (!this.member) {
          this.member = { id: this.currentUserId };
        }

        this.competencies = rows.filter(
            (assignment) =>
                assignment &&
                typeof assignment === "object" &&
                assignment.id !== undefined &&
                assignment.id !== null
        );
        this.total = Number.isFinite(total)
            ? total
            : Number(this.member?.competencies_count) || this.competencies.length;
        this.page = Number(meta.current_page) || this.page;
        this.totalPages = Math.max(
            1,
            Number(meta.last_page) ||
            Math.ceil(this.total / this.perPage) ||
            1
        );
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        const status = error?.response?.status;
        this.competencies = [];
        this.total = 0;
        this.totalPages = 1;

        if (status === 404) {
          this.notFound = true;
          this.member = null;
          return;
        }

        const message =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("general.try_later");

        if (initial || !this.member) {
          this.pageError = message;
          this.member = null;
        } else {
          this.listError = message;
        }
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    changePage(page) {
      const nextPage = Number(page);
      if (
          !Number.isFinite(nextPage) ||
          nextPage < 1 ||
          nextPage > this.totalPages ||
          nextPage === this.page
      ) {
        return;
      }
      this.page = nextPage;
      this.loadCompetencies();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    openCompetency(assignment) {
      if (!assignment?.id || !this.hasValidUserId()) return;
      this.$router.push({
        name: "user.my-team.competency",
        params: {
          userId: String(this.currentUserId),
          userCompetencyId: String(assignment.id),
        },
      });
    },
    displayValue(value) {
      return value === undefined || value === null || String(value).trim() === ""
          ? "—"
          : String(value);
    },
    formatDate(value) {
      if (!value) return "-";
      const date = moment(value);
      return date.isValid() ? date.format("DD.MM.YYYY") : "-";
    },
  },
};
</script>

<style scoped>
.team-member-page {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 8px 0 28px;
  box-sizing: border-box;
  overflow-x: hidden;
}

.member-summary {
  display: grid;
  grid-template-columns: minmax(240px, 0.8fr) minmax(0, 2fr);
  gap: 24px;
  padding: 20px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(30, 41, 59, 0.04);
}

.member-summary__identity {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.member-summary__identity > div:not(.avatar) {
  flex: 1 1 auto;
  min-width: 0;
}

.member-summary-avatar {
  width: 62px;
  height: 62px;
  min-width: 62px;
  flex-shrink: 0;
  font-size: 20px;
}

.member-summary__identity strong,
.member-summary__identity a {
  display: block;
}

.member-summary__identity strong {
  margin-bottom: 4px;
  color: #1e293b;
  font-size: 17px;
}

.member-summary__identity a {
  overflow: hidden;
  color: var(--backgroundColor, #666cff);
  font-size: 13px;
  text-overflow: ellipsis;
}

.member-summary__organization {
  display: grid;
  grid-template-columns: repeat(5, minmax(100px, 1fr));
  gap: 16px;
  margin: 0;
}

.member-summary__organization dt {
  margin-bottom: 4px;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.member-summary__organization dd {
  margin: 0;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.member-controls {
  display: flex;
  align-items: stretch;
  gap: 14px;
  margin-bottom: 18px;
}

.member-total,
.member-toolbar {
  display: flex;
  align-items: center;
  min-height: 70px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  box-sizing: border-box;
}

.member-total {
  flex: 0 0 auto;
  min-width: 210px;
  width: max-content;
  max-width: 100%;
  gap: 9px;
  color: #64748b;
  font-size: 12px;
  white-space: nowrap;
}

.member-total span {
  flex: 0 1 auto;
  min-width: 0;
}

.member-total i {
  flex: 0 0 auto;
  color: var(--backgroundColor, #666cff);
  font-size: 23px;
}

.member-total strong {
  flex: 0 0 auto;
  margin-left: auto;
  padding-left: 12px;
  color: #1e293b;
  font-size: 22px;
}

.member-toolbar {
  flex: 1 1 auto;
  min-width: 0;
  justify-content: space-between;
  gap: 14px;
}

.team-member-page .member-toolbar .member-search {
  position: relative;
  flex: 1;
  max-width: 520px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 12px;
  margin: 0;
  overflow: hidden;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  color: #64748b;
  background: #fff;
  box-shadow: none;
  box-sizing: border-box;
}

.team-member-page .member-toolbar .member-search:focus-within {
  border-color: var(--backgroundColor, #666cff);
  box-shadow: 0 0 0 3px rgba(102, 108, 255, 0.1);
}

.team-member-page .member-toolbar .member-search > i {
  flex: 0 0 auto;
  margin: 0;
  font-size: 17px;
  line-height: 1;
}

.team-member-page .member-toolbar .member-search > input[type="search"] {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  max-width: none;
  height: 40px !important;
  min-height: 40px !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  border-radius: 0;
  outline: 0;
  color: #1e293b;
  background: transparent;
  box-shadow: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4 !important;
  opacity: 1;
  appearance: none;
  -webkit-appearance: none;
  display: block;
}

.team-member-page .member-toolbar .member-search > input[type="search"]:focus {
  border: 0;
  outline: 0;
  background: transparent;
  box-shadow: none;
}

.team-member-page .member-toolbar .member-search > input[type="search"]::placeholder {
  color: #94a3b8;
  font-weight: 400;
  opacity: 1;
}

.member-sort {
  flex: 0 0 220px;
  width: 220px;
  max-width: 220px;
  margin: 0;
}

.member-sort :deep(.form-group) {
  width: 100%;
  margin: 0;
}

.member-sort :deep(.multiselect) {
  height: 42px !important;
  min-height: 42px !important;
  max-height: 42px !important;
}

.member-sort :deep(.multiselect__tags) {
  box-sizing: border-box;
  height: 42px !important;
  min-height: 42px !important;
  max-height: 42px !important;
  padding: 8px 40px 0 12px;
  overflow: hidden;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
}

.member-sort :deep(.multiselect__select) {
  height: 40px;
}

.member-sort :deep(.multiselect__placeholder),
.member-sort :deep(.multiselect__single) {
  box-sizing: border-box !important;
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 24px !important;
  min-height: 24px !important;
  margin: 0 0 8px !important;
  padding: 0 !important;
  overflow: hidden !important;
  border: 0 !important;
  background: transparent !important;
  color: #1e293b !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 24px !important;
  white-space: nowrap !important;
  text-overflow: ellipsis !important;
}

.member-sort :deep(.multiselect__input) {
  position: absolute !important;
  width: 0 !important;
  min-width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  overflow: hidden !important;
}

.member-sort :deep(.multiselect--active .multiselect__tags) {
  border-color: var(--backgroundColor, #666cff);
  box-shadow: 0 0 0 3px rgba(102, 108, 255, 0.1);
}

.member-summary__info {
  display: none;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0 0 0 auto;
  padding: 0;
  color: var(--backgroundColor, #666cff);
  background: rgba(102, 108, 255, 0.12);
  border: 1px solid rgba(102, 108, 255, 0.35);
  border-radius: 10px;
  cursor: pointer;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
}

.member-summary__info:active {
  color: #fff;
  background: var(--backgroundColor, #666cff);
  border-color: var(--backgroundColor, #666cff);
}

.member-summary__info i {
  font-size: 20px;
}

.team-member-page :deep(.app-modal-title) {
  flex: 1 1 auto;
  min-width: 0;
  max-width: calc(100% - 44px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 800px) {
  .team-member-page :deep(.app-modal-close) {
    flex: 0 0 48px;
    width: 48px;
    min-width: 48px;
    height: 48px;
    min-height: 48px;
    padding: 0;
    color: #475569;
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    border-radius: 50%;
    font-size: 28px;
  }

  .team-member-page :deep(.app-modal-close span) {
    display: block;
    line-height: 1;
  }

  .team-member-page :deep(.app-modal-title) {
    max-width: calc(100% - 48px);
  }
}

.member-org-meta {
  display: grid;
  margin: 0;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  overflow: hidden;
}

.member-org-meta > div {
  display: grid;
  grid-template-columns: minmax(0, 110px) minmax(0, 1fr);
  align-items: start;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #eef2f7;
}

.member-org-meta > div:last-child {
  border-bottom: 0;
}

.member-org-meta dt {
  min-width: 0;
  margin: 0;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  overflow-wrap: anywhere;
}

.member-org-meta dd {
  min-width: 0;
  margin: 0;
  color: #1e293b;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.member-org-close {
  min-height: 38px;
  padding: 0 17px;
  color: #475569;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
}

.member-competency-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.member-competency-list {
  display: none;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
}

.competency-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 12px;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.competency-row:last-child {
  border-bottom: 0;
}

.competency-row:active {
  background: #f8fafc;
}

.competency-row__icon {
  display: inline-flex;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  color: var(--backgroundColor, #3568d4);
  background: #edf3ff;
  border-radius: 50%;
}

.competency-row__icon i {
  font-size: 20px;
}

.competency-row__body {
  flex: 1;
  min-width: 0;
}

.competency-row__body strong,
.competency-row__body p,
.competency-row__body small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.competency-row__body strong {
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.competency-row__body p {
  margin: 3px 0 0;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: normal;
}

.competency-row__body small {
  margin-top: 4px;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
}

.competency-row__chevron {
  flex: 0 0 auto;
  color: #cbd5e1;
  font-size: 22px;
}

.competency-row--skeleton {
  cursor: default;
}

.member-summary--skeleton {
  display: flex;
  align-items: center;
  gap: 14px;
}

.member-summary-skeleton__lines {
  flex: 1;
  min-width: 0;
}

.skl {
  display: block;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: member-skeleton-loading 1.5s infinite;
}

.skl--avatar {
  flex: 0 0 62px;
  width: 62px;
  height: 62px;
  border-radius: 50%;
}

.skl--icon {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
}

.skl--title {
  width: 55%;
  max-width: 240px;
  height: 15px;
}

.skl--line {
  width: 80%;
  max-width: 340px;
  height: 12px;
  margin-top: 8px;
}

.skl--meta {
  width: 40%;
  max-width: 130px;
  height: 10px;
  margin-top: 8px;
}

@keyframes member-skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.member-pagination {
  margin-top: 18px;
}

.team-page-state,
.team-list-state {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  color: #64748b;
  text-align: center;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
}

.team-list-state {
  min-height: 200px;
}

.team-page-state > i,
.team-list-state > i {
  color: #94a3b8;
  font-size: 36px;
}

.team-page-state strong,
.team-list-state strong {
  display: block;
  color: #334155;
}

.team-page-state p,
.team-list-state p {
  margin: 4px 0 0;
}

.team-page-state--empty,
.team-list-state--empty {
  flex-direction: column;
}

.team-page-state--error,
.team-list-state--error {
  flex-wrap: wrap;
}

.team-page-state--error > i,
.team-list-state--error > i {
  color: #dc2626;
}

.team-state-button,
.team-state-link {
  padding: 9px 14px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: var(--backgroundColor, #666cff);
  cursor: pointer;
  text-decoration: none;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1180px) {
  .member-summary {
    grid-template-columns: 1fr;
  }

  .member-summary__organization {
    grid-template-columns: repeat(3, minmax(100px, 1fr));
  }

  .member-competency-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 800px) {
  .team-member-page {
    padding-top: 4px;
  }

  .member-summary {
    padding: 12px;
    margin-bottom: 12px;
  }

  .member-summary-avatar {
    width: 44px;
    height: 44px;
    min-width: 44px;
    font-size: 14px;
  }

  .member-summary--skeleton .skl--avatar {
    flex-basis: 44px;
    width: 44px;
    height: 44px;
  }

  .member-summary__identity strong {
    font-size: 15px;
  }

  .member-summary__organization {
    display: none;
  }

  .member-summary__identity {
    gap: 10px;
  }

  .member-summary__identity strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .member-summary__identity a {
    max-width: 100%;
  }

  .member-summary__info {
    display: inline-flex;
  }

  .member-controls {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 12px;
  }

  .member-total {
    width: 100%;
    min-width: 0;
    min-height: 0;
    padding: 10px 14px;
  }

  .member-total strong {
    font-size: 18px;
  }

  .member-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    padding: 12px;
  }

  .team-member-page .member-toolbar .member-search {
    max-width: none;
    height: 48px;
    min-height: 48px;
  }

  .team-member-page .member-toolbar .member-search > input[type="search"] {
    height: 46px !important;
    min-height: 46px !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.4 !important;
    font-size: 16px !important;
  }

  .member-search,
  .member-sort {
    width: 100%;
    max-width: none;
    box-sizing: border-box;
  }

  .member-sort {
    flex: 1 1 auto;
    min-width: 0;
  }

  .member-sort :deep(.multiselect) {
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
  }

  .member-sort :deep(.multiselect__tags) {
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
    padding: 11px 40px 0 12px;
  }

  .member-sort :deep(.multiselect__select) {
    height: 46px;
  }

  .member-sort :deep(.multiselect__placeholder),
  .member-sort :deep(.multiselect__single) {
    height: 26px !important;
    min-height: 26px !important;
    line-height: 26px !important;
  }

  .member-competency-grid {
    display: none;
  }

  .member-competency-list {
    display: block;
    width: 100%;
    box-sizing: border-box;
  }

  .team-page-state,
  .team-list-state {
    min-height: 180px;
    padding: 20px;
  }

  .member-pagination {
    margin-top: 14px;
  }
}

@media (max-width: 480px) {
  .team-member-page {
    padding-bottom: 20px;
  }

  .member-summary {
    padding: 12px;
  }

  .member-summary__info {
    flex-basis: 36px;
    width: 36px;
    height: 36px;
  }

  .member-summary__info i {
    font-size: 18px;
  }

  .member-org-meta > div {
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 10px 12px;
  }

  .competency-row {
    gap: 9px;
    padding: 12px 10px;
  }

  .competency-row__icon {
    flex-basis: 38px;
    width: 38px;
    height: 38px;
  }

  .competency-row__icon i {
    font-size: 18px;
  }

  .competency-row__body strong {
    font-size: 13px;
  }

  .competency-row__body p {
    font-size: 11px;
  }

  .competency-row__chevron {
    font-size: 20px;
  }

  .team-page-state,
  .team-list-state {
    padding: 24px 14px;
  }
}
</style>
